import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Heart, ExternalLink } from "lucide-react";
import { donate, useDonateStatus } from "../lib/donate";
import { toast } from "sonner";
import { trackEvent } from "../lib/analytics";
import { detectCurrency, convertFromGBP, formatMoney, loadFxRates } from "../lib/currency";

const TIER_LABELS = {
  tier_3: "Coffee",
  tier_10: "Lunch",
  tier_25: "Dinner",
};

const CURRENCY_FLAGS = {
  GBP: "🇬🇧", USD: "🇺🇸", EUR: "🇪🇺", CAD: "🇨🇦", AUD: "🇦🇺", INR: "🇮🇳",
  NGN: "🇳🇬", ZAR: "🇿🇦", JPY: "🇯🇵", SGD: "🇸🇬", AED: "🇦🇪", CHF: "🇨🇭",
  BRL: "🇧🇷", MXN: "🇲🇽",
};

export default function DonateModal({ open, onClose }) {
  const status = useDonateStatus();
  const [selected, setSelected] = useState("tier_10");
  const [custom, setCustom] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [localCur] = useState(() => detectCurrency());
  const [payInLocal, setPayInLocal] = useState(() => detectCurrency() !== "GBP");
  const [, setFxTick] = useState(0);

  useEffect(() => { if (open) { setSelected("tier_10"); setCustom(""); loadFxRates().then(() => setFxTick(t => t + 1)); } }, [open]);

  async function go() {
    if (!status.configured) return;
    setBusy(true);
    try {
      const payload = {
        package_id: selected,
        origin_url: window.location.origin,
        email: email || null,
        currency: payInLocal && localCur !== "GBP" && (status.supported_currencies || []).includes(localCur) ? localCur : "GBP",
      };
      if (selected === "custom") {
        const amt = Number(custom);
        if (!amt || amt < status.custom.min || amt > status.custom.max) {
          toast.error(`Enter an amount between £${status.custom.min} and £${status.custom.max}`);
          setBusy(false); return;
        }
        payload.custom_amount = amt;
      }
      trackEvent("donate_click", { tier: selected });
      const res = await donate.checkout(payload);
      if (res.url) window.location.href = res.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start donation");
    } finally {
      setBusy(false);
    }
  }

  if (!status.configured) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="donate-modal">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight flex items-center gap-2">
            <Heart className="h-5 w-5 text-[color:var(--accent)]" /> Support VeriHandi
          </DialogTitle>
          <DialogDescription className="sr-only">
            Choose a donation tier or custom amount. Payment is processed securely by Stripe in GBP.
          </DialogDescription>
        </DialogHeader>
        <p className="text-sm" style={{ color: "var(--text-muted)" }}>
          VeriHandi is free and open. Your donation keeps the lights on — and funds new tools, the AI label reader, and our research. Pay securely via Stripe.
        </p>

        <div className="grid grid-cols-3 gap-2" data-testid="donate-tiers">
          {status.tiers.map(t => {
            const localAmt = localCur !== "GBP" ? convertFromGBP(t.amount, localCur) : null;
            return (
              <button
                key={t.id}
                onClick={() => setSelected(t.id)}
                data-testid={`donate-tier-${t.id}`}
                className={`rounded-xl border-2 p-3 text-center transition-colors ${selected === t.id ? "" : "hover:bg-[color:var(--surface-2)]"}`}
                style={{
                  borderColor: selected === t.id ? "var(--primary)" : "var(--border-subtle)",
                  background: selected === t.id ? "color-mix(in srgb, var(--primary) 10%, transparent)" : "transparent",
                }}
              >
                <div className="font-display font-black text-xl">£{t.amount}</div>
                <div className="text-xs" style={{ color: "var(--text-muted)" }}>
                  {TIER_LABELS[t.id] || "Tier"}
                  {localAmt != null && <div className="text-[10px] mt-0.5">≈ {formatMoney(localAmt, localCur)}</div>}
                </div>
              </button>
            );
          })}
        </div>

        <button
          onClick={() => setSelected("custom")}
          data-testid="donate-tier-custom"
          className={`w-full rounded-xl border-2 p-3 text-left transition-colors ${selected === "custom" ? "" : "hover:bg-[color:var(--surface-2)]"}`}
          style={{
            borderColor: selected === "custom" ? "var(--primary)" : "var(--border-subtle)",
            background: selected === "custom" ? "color-mix(in srgb, var(--primary) 10%, transparent)" : "transparent",
          }}
        >
          <div className="flex items-center justify-between">
            <span className="font-semibold">Custom amount</span>
            {selected === "custom" && (
              <div className="flex items-center gap-1">
                <span className="font-semibold">£</span>
                <Input
                  type="number"
                  min={status.custom.min}
                  max={status.custom.max}
                  value={custom}
                  onChange={(e) => setCustom(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  placeholder="50"
                  data-testid="donate-custom-amount"
                  className="w-24 h-8"
                />
              </div>
            )}
          </div>
        </button>

        <div>
          <label className="label-caps mb-1 block">Email (optional, for receipt)</label>
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" data-testid="donate-email" />
        </div>

        <Button onClick={go} disabled={busy} data-testid="donate-submit" className="w-full text-white font-semibold" style={{ background: "var(--accent)" }}>
          {busy ? "Starting checkout…" : <>Donate via Stripe <ExternalLink className="h-4 w-4 ml-2" /></>}
        </Button>
        {localCur !== "GBP" && (status.supported_currencies || []).includes(localCur) && (
          <label className="flex items-center justify-between gap-2 text-xs cursor-pointer px-1" data-testid="pay-local-toggle-label">
            <span style={{ color: "var(--text-muted)" }}>
              <span aria-hidden="true" className="text-base mr-1">{CURRENCY_FLAGS[localCur] || ""}</span>
              Pay in <b>{localCur}</b> <span aria-hidden="true" className="mx-1">→</span>
              <span aria-hidden="true" className="text-base mr-1">{CURRENCY_FLAGS.GBP}</span>
              <span>(no bank conversion)</span>
            </span>
            <input
              type="checkbox"
              checked={payInLocal}
              onChange={(e) => setPayInLocal(e.target.checked)}
              data-testid="pay-local-toggle"
              className="accent-[color:var(--primary)]"
            />
          </label>
        )}
        <p className="text-xs text-center" style={{ color: "var(--text-muted)" }}>
          Secure payment by Stripe · {payInLocal && localCur !== "GBP" && (status.supported_currencies || []).includes(localCur)
            ? <>Charged in <b>{localCur}</b> <span aria-hidden="true">{CURRENCY_FLAGS[localCur] || ""}</span></>
            : <>Charged in GBP <span aria-hidden="true">🇬🇧</span>{localCur !== "GBP" ? <> (your bank converts to {localCur} <span aria-hidden="true">{CURRENCY_FLAGS[localCur] || ""}</span>)</> : null}</>} · One-time
        </p>
      </DialogContent>
    </Dialog>
  );
}
